import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  ProfileState,
  LoadProfileAction,
  SetProfileAction,
  FollowAction,
  UnfollowAction,
  SetFollowStatus,
  EditProfileAction,
  SetStatusAction,
  SendInviteAction,
  LoadMorePostsAction,
  SetLoadMorePostsAction,
  UploadPhotoAction,
  CheckDomainAction,
  ChangeDomainAction,
  IncrementScheduledCountAction,
  SetProfileFailedAction,
  SaveCountriesAction,
  SaveNotifySettingsAction,
  ChangeContentPropertyAction,
  ChangePasswordAction,
  ToggleIsWatermarkEnabledAction,
  LoadFilteredPostsAction,
  SetFilteredPostsLoadedAction,
  MutateProfileStateValueAction,
  ProfileInfo,
  SetProfileCoverAction,
  SetCoverUrlAction,
  UploadCoverAction,
  ChangeGenderFilterAction,
  SetAuthPopoverStateAction,
  RemoveSkipWelcomeVideoProfilesAction,
  AddSkipWelcomeVideoProfilesAction,
  SetWelcomeVideoEditAction,
  RemoveStreamAction,
  AddBundleIdAction,
  RemoveBundleIdAction,
  SendProfileViewAction,
} from './interface';
import { RootState } from 'store/rootReducer';
import { getStorageItem, setStorageItem } from '$utils';

const skipWelcomeVideoProfilesString = getStorageItem
  ? getStorageItem('skipWelcomeVideoProfiles')
  : null;
const skipWelcomeVideoProfiles = skipWelcomeVideoProfilesString
  ? skipWelcomeVideoProfilesString.split(',').map(parseInt)
  : [];

const initialState: ProfileState = {
  userId: 0,
  isLoading: true,
  profileInfo: {},
  profileFailed: {},
  coverUrl: null,
  authPopoverState: null,
  welcomeVideoEdit: false,
  skipWelcomeVideoProfiles,
};

const mutateProfileInfoValueCount = (
  profileInfo: ProfileInfo,
  key: keyof ProfileInfo,
  operation: 'inc' | 'dec',
) => {
  if (profileInfo) {
    profileInfo[key as string] += operation === 'inc' ? 1 : -1;
  }
};

export const { actions, reducer } = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    loadProfile(state, action: LoadProfileAction) {
      state.userId = action.payload.ownerId;
      state.isLoading = true;
      delete state.profileFailed[action.payload.ownerId];
    },
    setProfileFailed(state, action: SetProfileFailedAction) {
      state.userId = action.payload.userId;
      state.isLoading = false;
      state.profileFailed[action.payload.userId] = action.payload.code;
    },
    removeStream(state, action: RemoveStreamAction) {
      if (!state.profileInfo[action.payload]) return;
      state.profileInfo[action.payload].stream = undefined;
    },
    setProfile(state, action: SetProfileAction) {
      state.userId = action.payload.userId;
      state.isLoading = false;

      if (
        action.payload.isFollowFromMe &&
        !state.skipWelcomeVideoProfiles.includes(action.payload.userId)
      ) {
        state.skipWelcomeVideoProfiles.push(action.payload.userId);
      }

      state.profileInfo[action.payload.userId] = {
        isFollowFromMe: action.payload.isFollowFromMe,
        isFollowToMe: action.payload.isFollowToMe,
        postsCount: action.payload.postsCount,
        reelsCount: action.payload.reelsCount,
        postsNextFrom: action.payload.postsNextFrom,
        isPostsLoadingMore: false,
        scheduledCount: action.payload.scheduledCount,
        animeListsCount: action.payload.animeListsCount,
        sponsoredPostsCount: action.payload.sponsoredPostsCount,
        totalLikesCount: action.payload.totalLikesCount,
        subscriptionMessage: action.payload.subscriptionMessage,
        subscriptionPrice: action.payload.subscriptionPrice,
        nftAddress: action.payload.nftAddress,
        coverUrl: action.payload.coverUrl,
        bundleIds: action.payload.bundleIds,
        photoWidth: action.payload.photoWidth,
        photoHeight: action.payload.photoHeight,
        coverThumbnail: action.payload.coverThumbnail,
        sponsorsContentCount: action.payload.sponsorsContentCount,
        subscriptionEurPrice: action.payload.subscriptionEurPrice,
        welcomeVideo: action.payload.welcomeVideo,
        stream: action.payload.stream,
        subscriptionDiscount: action.payload.subscriptionDiscount,
        isBannedFromUser: action.payload.isBannedFromUser,
        squad: action.payload.squad,
        leaderboardPosition: action.payload.leaderboardPosition,
        wonLeaderboards: action.payload.wonLeaderboards,
      };
    },
    incrementScheduledCount(state, action: IncrementScheduledCountAction) {
      const { userId, value = 1 } = action.payload;
      if (state.profileInfo[userId]) {
        state.profileInfo[userId].scheduledCount += value;
      }
    },
    addBundleId(state, action: AddBundleIdAction) {
      const { userId, bundleId } = action.payload;
      const user = state.profileInfo[userId];
      if (user) {
        user.bundleIds.push(bundleId);
      }
    },
    removeBundleId(state, action: RemoveBundleIdAction) {
      const { userId, bundleId } = action.payload;
      const user = state.profileInfo[userId];
      if (user) {
        user.bundleIds = user.bundleIds.filter((id) => id !== bundleId);
      }
    },
    follow(_, action: FollowAction) {},
    unfollow(_, action: UnfollowAction) {},
    setFollowStatus(state, action: SetFollowStatus) {
      const { userId, isFollowed } = action.payload;

      state.profileInfo[userId] = {
        ...(state.profileInfo[userId] || {}),
        isFollowFromMe: isFollowed,
      };
    },
    editProfile(_, action: EditProfileAction) {},
    setStatus(_, action: SetStatusAction) {},
    sendInvite(_, action: SendInviteAction) {},
    loadMorePosts(state, action: LoadMorePostsAction) {
      const { ownerId } = action.payload;

      state.profileInfo[ownerId].isPostsLoadingMore = true;
    },
    setLoadMorePosts(state, action: SetLoadMorePostsAction) {
      const { userId, nextFrom } = action.payload;

      if (state.profileInfo[userId]) {
        state.profileInfo[userId].isPostsLoadingMore = false;
        state.profileInfo[userId].postsNextFrom = nextFrom;
      }
    },
    loadFilteredPosts(state, action: LoadFilteredPostsAction) {
      const { ownerId } = action.payload;

      if (state.profileInfo[ownerId]) {
        state.profileInfo[ownerId].isPostsLoading = true;
      }
    },
    setFilteredPostsLoaded(state, action: SetFilteredPostsLoadedAction) {
      const { ownerId, nextFrom } = action.payload;

      if (state.profileInfo[ownerId]) {
        state.profileInfo[ownerId].isPostsLoading = false;
        state.profileInfo[ownerId].postsNextFrom = nextFrom;
      }
    },
    uploadPhoto(_, action: UploadPhotoAction) {},
    // chooseCover() {},
    deleteCover() {},
    setCoverUrl(state, action: SetCoverUrlAction) {
      state.coverUrl = action.payload;
    },
    setProfileCover(state, action: SetProfileCoverAction) {
      const { coverUrl, coverThumbnail } = action.payload;
      if (state.profileInfo[state.userId]) {
        state.profileInfo[state.userId].coverThumbnail = coverThumbnail;
        state.profileInfo[state.userId].coverUrl = coverUrl;
      }
    },
    deleteProfileCover(state) {
      if (state.profileInfo[state.userId]) {
        state.profileInfo[state.userId].coverThumbnail = undefined;
        state.profileInfo[state.userId].coverUrl = undefined;
      }
    },
    uploadCover(_, action: UploadCoverAction) {},
    checkDomain(_, action: CheckDomainAction) {},
    changeDomain(_, action: ChangeDomainAction) {},
    changePassword(_, action: ChangePasswordAction) {},
    resetPassword() {},
    changeContentPriority(_, action: ChangeContentPropertyAction) {},
    changeGenderPriority(_, action: ChangeGenderFilterAction) {},
    saveCountries(_, action: SaveCountriesAction) {},
    saveNotifySettings(_, __: SaveNotifySettingsAction) {},
    incrementReelsCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(state.profileInfo[action.payload], 'reelsCount', 'inc');
    },
    incrementPostsCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(state.profileInfo[action.payload], 'postsCount', 'inc');
    },
    decrementPostsCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(state.profileInfo[action.payload], 'postsCount', 'dec');
    },
    incrementTotalLikesCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(
        state.profileInfo[action.payload],
        'totalLikesCount',
        'inc',
      );
    },
    decrementTotalLikesCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(
        state.profileInfo[action.payload],
        'totalLikesCount',
        'dec',
      );
    },
    incrementSponsoredPostsCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(
        state.profileInfo[action.payload],
        'sponsoredPostsCount',
        'inc',
      );
    },
    decrementSponsoredPostsCount(state, action: MutateProfileStateValueAction) {
      mutateProfileInfoValueCount(
        state.profileInfo[action.payload],
        'sponsoredPostsCount',
        'dec',
      );
    },
    toggleIsWatermarkEnabled(_, action: ToggleIsWatermarkEnabledAction) {},
    setAuthPopoverState(state, action: SetAuthPopoverStateAction) {
      state.authPopoverState = action.payload;
    },
    clearWelcomeVideo(state) {
      state.profileInfo[state.userId].welcomeVideo = undefined;
    },
    removeSkipWelcomeVideoProfiles(state, action: RemoveSkipWelcomeVideoProfilesAction) {
      state.skipWelcomeVideoProfiles = state.skipWelcomeVideoProfiles.filter(
        (id) => id !== action.payload,
      );
      setStorageItem(
        'skipWelcomeVideoProfiles',
        state.skipWelcomeVideoProfiles.join(','),
      );
    },
    addSkipWelcomeVideoProfiles(state, action: AddSkipWelcomeVideoProfilesAction) {
      state.skipWelcomeVideoProfiles.push(action.payload);
      setStorageItem(
        'skipWelcomeVideoProfiles',
        state.skipWelcomeVideoProfiles.join(','),
      );
    },
    setWelcomeVideoEdit(state, action: SetWelcomeVideoEditAction) {
      state.welcomeVideoEdit = action.payload;
    },
    requestDeleteWelcomeVideo() {},
    deleteWelcomeVideo(state) {
      state.profileInfo[state.userId].welcomeVideo = undefined;
    },
    uploadWelcomeVideo() {},
    sendProfileView(_, __: SendProfileViewAction) {},
  },
});

export { reducer as profileReducer, actions as profileActions };

export const profileSelector = (state: RootState) => state.profile;

export const currentProfileSelector = (state: RootState) =>
  state.profile.profileInfo[state.profile.userId];

export const profileInfoSelector = createSelector(
  profileSelector,
  (_, profileId: number) => profileId,
  (profile, profileId) => {
    return profile.profileInfo[profileId] || { isEmpty: true };
  },
);

export const profileFailedSelector = createSelector(
  profileSelector,
  (_, profileId: number) => profileId,
  (profile, profileId) => {
    return profile.profileFailed[profileId];
  },
);
